import React, { useImperativeHandle, useRef } from 'react';
import useMessageDialog from '@Util/hook/useMessageDialog';
import UseDeviceOrderApi from '@Apis/useDeviceOrderApi';

/**
 * 設備租賃彈窗組件
 *
 * 功能：
 * - 提供批次付款任務的確認彈窗
 * - 支援刪除和扣款操作確認
 * - 使用 useImperativeHandle 暴露操作方法
 *
 * @component
 * @param {Object} props - 組件屬性
 * @param {Object} ref - 組件引用
 * @returns {JSX.Element} 設備租賃彈窗組件
 */
const DeviceRentalDialogBox = React.forwardRef((props, ref) => {
    const { postDeviceOrderTasksSettleApi, deleteDeviceOrderTasksApi } = UseDeviceOrderApi();
    const [
        // openDialog
        , setOpenDialog] = React.useState("");
    const sourceDataRef = useRef({});
    const refreshRef = useRef(null);
    const setMessageDialog = useMessageDialog();

    /**
     * 執行刷新操作
     */
    const doRefresh = () => {
        if (refreshRef.current && typeof refreshRef.current === 'function') refreshRef.current();
    };

    useImperativeHandle(
        ref,
        () => {
            return {
                openConfirmDeleteBatchPaymentTaskDialog: (targetData = {}, _refreshFn = () => {}) => {
                    const { taskID } = targetData;

                    refreshRef.current = _refreshFn;

                    const doDeleteApi = async (taskID, onClose) => {
                        const resp = await deleteDeviceOrderTasksApi(taskID);
                        if (resp) {
                            doRefresh();
                            typeof onClose === 'function' && onClose();
                        }
                    };

                    setMessageDialog({
                        open: true,
                        title: '確認刪除',
                        msg: '確定要刪除這批「設備租賃」訂單嗎？刪除後將無法復原，請再次確認。',
                        onAsyncConfirm: (_, onClose) => doDeleteApi(taskID, onClose),
                    });
                },
                openConfirmSettleBatchPaymentTaskDialog: (targetData = {}, _refreshFn = () => {}) => {
                    const { taskID } = targetData;

                    refreshRef.current = _refreshFn;

                    const doSettleCyclePurchaseTaskApi = async (taskID, onClose) => {
                        const resp = await postDeviceOrderTasksSettleApi(taskID);
                        if (resp) {
                            doRefresh();
                            typeof onClose === 'function' && onClose();
                        }
                    };

                    setMessageDialog({
                        open: true,
                        title: '確認扣款',
                        msg: '您確定要對這批「設備租賃」訂單執行銀行扣款嗎？',
                        onAsyncConfirm: (_, onClose) => doSettleCyclePurchaseTaskApi(taskID, onClose),
                    });
                },
                closeDialog: () => handleCloseDialog(),
            };
        },
        // eslint-disable-next-line
        [],
    );

    /**
     * 關閉彈窗
     */
    const handleCloseDialog = () => {
        refreshRef.current = null;
        sourceDataRef.current = {};
        setOpenDialog('');
    };

    return null;
});

export default DeviceRentalDialogBox;
